import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Button, TextField, InputAdornment, Chip, IconButton, Menu, MenuItem, Typography, Avatar } from '@mui/material';
import ErpDataGrid from '../../components/common/ErpDataGrid';
import { Add as AddIcon, Search as SearchIcon, MoreVert as MoreVertIcon, Edit as EditIcon, Delete as DeleteIcon, LockReset as ResetIcon, Shield as ProtectedIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const UsersList = () => {
  const navigate = useNavigate();
  const { hasPermission, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/users', { params: { page: pagination.page + 1, limit: pagination.pageSize, search } });
      setUsers(r.data.data);
      setPagination(p => ({ ...p, total: r.data.pagination.total }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleMenuOpen = (e, user) => { setAnchorEl(e.currentTarget); setSelectedUser(user); };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedUser(null); };

  const handleDelete = async () => {
    if (!selectedUser || selectedUser.id === currentUser.id) { toast.error("Can't delete yourself"); handleMenuClose(); return; }
    if (selectedUser.is_system_admin) { toast.error("Cannot delete the system administrator. This account is protected."); handleMenuClose(); return; }
    if (!window.confirm('Delete this user?')) { handleMenuClose(); return; }
    try { await api.delete(`/users/${selectedUser.id}`); toast.success('User deleted'); fetchUsers(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
    handleMenuClose();
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Reset password for this user?')) { handleMenuClose(); return; }
    try { await api.post(`/users/${selectedUser.id}/reset-password`); toast.success('Password reset email sent'); }
    catch (e) { toast.error('Failed to reset'); }
    handleMenuClose();
  };

  const columns = [
    {
      field: 'user_reference',
      headerName: 'Reference',
      width: 200,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}>
          {p.row.user_reference || '—'}
        </Typography>
      )
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200, renderCell: (p) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.9rem' }}>{p.row.first_name?.[0]}{p.row.last_name?.[0]}</Avatar>
        <Box><Typography variant="body2" sx={{ fontWeight: 500 }}>{p.row.first_name} {p.row.last_name}</Typography><Typography variant="caption" color="text.secondary">{p.row.email}</Typography></Box>
      </Box>
    )},
    { field: 'groups', headerName: 'Groups', width: 200, renderCell: (p) => p.row.groups?.map(g => <Chip key={g.id} label={g.name} size="small" sx={{ mr: 0.5 }} />) },
    { field: 'is_admin', headerName: 'Admin', width: 120, renderCell: (p) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {p.value ? <Chip label="Yes" size="small" color="primary" /> : '-'}
        {p.row.is_system_admin && <Chip icon={<ProtectedIcon />} label="Protected" size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }} />}
      </Box>
    )},
    { field: 'is_active', headerName: 'Status', width: 100, renderCell: (p) => <Chip label={p.value ? 'Active' : 'Inactive'} size="small" sx={{ bgcolor: p.value ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)', color: p.value ? '#10b981' : '#6b7280' }} /> },
    { field: 'otp_enabled', headerName: '2FA', width: 60, renderCell: (p) => p.value ? '✓' : '-' },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (p) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e, p.row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box><Typography variant="h4" sx={{ fontWeight: 700 }}>Users</Typography><Typography variant="body2" color="text.secondary">Manage system users</Typography></Box>
        {hasPermission('users_add') && (<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/users/new')} sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>Add User</Button>)}
      </Box>

      <Card sx={{ mb: 3, p: 2 }}>
        <TextField placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} size="small" sx={{ minWidth: 300 }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />
      </Card>

      <Card>
        <ErpDataGrid
          rows={users}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={pagination.total}
          paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
          onPaginationModelChange={(m) => setPagination({ ...pagination, ...m })}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          onRowClick={(params) => navigate(`/admin/users/${params.row.id}`)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { navigate(`/admin/users/${selectedUser?.id}`); handleMenuClose(); }}><ViewIcon sx={{ mr: 1, fontSize: 20 }} /> View</MenuItem>
        {hasPermission('users_modify') && (<MenuItem onClick={() => { navigate(`/admin/users/${selectedUser?.id}/edit`); handleMenuClose(); }}><EditIcon sx={{ mr: 1, fontSize: 20 }} /> Edit</MenuItem>)}
        {hasPermission('users_modify') && (<MenuItem onClick={handleResetPassword}><ResetIcon sx={{ mr: 1, fontSize: 20 }} /> Reset Password</MenuItem>)}
        {hasPermission('users_delete') && !selectedUser?.is_system_admin && (<MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}><DeleteIcon sx={{ mr: 1, fontSize: 20 }} /> Delete</MenuItem>)}
        {selectedUser?.is_system_admin && (<MenuItem disabled sx={{ color: 'text.secondary', fontSize: '0.85rem' }}><ProtectedIcon sx={{ mr: 1, fontSize: 18 }} /> Protected Account</MenuItem>)}
      </Menu>
    </Box>
  );
};

export default UsersList;
